import {
  createEffect,
  createMemo,
  createSignal,
  For,
  type JSX,
  onCleanup,
  onMount,
  Show,
} from 'solid-js';
import { Portal } from 'solid-js/web';
import { t } from '../i18n';
import { type AcceptedMoves, useAcceptedMoves } from '../lib/accepted-move';
import type { AcceptedMove, MovePage, MoveRefused } from '../lib/move-page';
import { useMovePage } from '../lib/move-page';
import type { AcceptedMoveRef } from '../lib/move-read';
import { prefersReducedMotion } from '../lib/reduced-motion';
import { transferSlot } from '../lib/transfer-slot';
import { playTransferSound } from '../lib/transfer-sound';
import { type CardPlace, stackLayout } from '../lib/transfer-stack';
import {
  createTransferStack,
  type TransferStackDeps,
  type TransferStackStore,
} from '../lib/transfer-stack-store';
import { TransferCard } from './TransferCard';

// THE TRANSFER STATUS STACK (#6773, epic #6552; prototype mvdHost through mvdLayout,
// `docs/design/fleet-redesign/prototype.html:3101-3207`): a card per move this page sent, bottom
// right, directly above the floating Core. Rendered once inside `FleetMachines`' providers, and
// only for a person who may move work (`canAdminWrite`): it shows only this page's own moves.
//
// IT PORTALS INTO THE SLOT (`#fleet-live-transfers`, claimed by `FleetLivePip`), which
// `.fleet-live__float` positions; the stack sets only its width and never finds the floating
// Core through the DOM. While no slot is rendered (Fleet Live has no body yet), the cards stay in
// the store, their timers, follows and Undo running, and the stack renders nothing; a new slot
// gets a new portal with every live card. The portal exists only while there are cards, so an
// empty slot stays `:empty`.
//
// IT HANDS THE PAGE ITS SINK (`onSink`): `FleetMachines` forwards each 201 and each refusal to
// it, and to nothing while the stack is not mounted.
//
// OPEN (expanded, and nothing clears) when pinned by Expand, hovered by a non-touch pointer, or
// focused within. Expanded, the cards fan up to `max(180, innerHeight - bottom - 120)` px, where
// `bottom` is read from the slot's rect on expand and on resize.

/** What `FleetMachines` hands each 201 and each refusal to. */
export type TransferSink = {
  readonly accepted: (a: AcceptedMove, ref: AcceptedMoveRef, order: number) => void;
  readonly refused: (r: MoveRefused) => void;
};

export type TransferStackProps = {
  /** Called with the sink on mount, and with null on unmount. */
  readonly onSink: (sink: TransferSink | null) => void;
  /** Test seams; the browser's clock, timers, motion preference and sounds by default. */
  readonly seams?: Partial<Pick<TransferStackDeps, 'now' | 'timers' | 'reduced' | 'sound'>>;
};

const browserTimers: TransferStackDeps['timers'] = {
  set: (fn, ms) => setTimeout(fn, ms),
  clear: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
};

export function TransferStack(props: TransferStackProps): JSX.Element {
  const page = useMovePage();
  const accepted = useAcceptedMoves();
  if (page === null || accepted === null) return null;
  const store = createTransferStack({
    page: () => page,
    accepted: () => accepted,
    now: props.seams?.now ?? (() => Date.now()),
    timers: props.seams?.timers ?? browserTimers,
    reduced: props.seams?.reduced ?? prefersReducedMotion,
    sound: props.seams?.sound ?? ((kind) => void playTransferSound(kind)),
  });
  props.onSink({ accepted: store.accepted, refused: store.refused });
  onCleanup(() => props.onSink(null));
  const slot = (): HTMLElement | null =>
    store.entries().length + store.leaving().length > 0 ? transferSlot() : null;
  return (
    <Show when={slot()} keyed>
      {(el) => (
        <Portal mount={el}>
          <StackView store={store} page={page} accepted={accepted} slot={el} />
        </Portal>
      )}
    </Show>
  );
}

function StackView(props: {
  readonly store: TransferStackStore;
  readonly page: MovePage;
  readonly accepted: AcceptedMoves;
  readonly slot: HTMLElement;
}): JSX.Element {
  const s = props.store;
  const reduced = prefersReducedMotion();
  const [heights, setHeights] = createSignal<ReadonlyMap<number, number>>(new Map());
  const [maxH, setMaxH] = createSignal(180);
  const measureRoom = (): void => {
    const bottom = window.innerHeight - props.slot.getBoundingClientRect().bottom;
    setMaxH(Math.max(180, window.innerHeight - bottom - 120));
  };
  onMount(() => {
    measureRoom();
    window.addEventListener('resize', measureRoom);
  });
  onCleanup(() => window.removeEventListener('resize', measureRoom));
  const expanded = (): boolean => s.open() && s.entries().length > 1;
  const layout = createMemo(() => {
    const hs = heights();
    return stackLayout(
      s.entries().map((e) => hs.get(e.card.id) ?? 0),
      expanded(),
      maxH(),
    );
  });
  const placeOf = (id: number) => {
    const i = s.entries().findIndex((e) => e.card.id === id);
    return i < 0 ? undefined : layout().places[i];
  };
  // Where each card last sat, so one leaving slides out from there. Pruned to the live and the
  // leaving cards on every layout, so it holds at most those.
  const lastPlace = new Map<number, CardPlace>();
  createEffect(() => {
    const keep = new Set([...s.entries(), ...s.leaving()].map((e) => e.card.id));
    for (const id of [...lastPlace.keys()]) if (!keep.has(id)) lastPlace.delete(id);
    s.entries().forEach((e, i) => {
      const p = layout().places[i];
      if (p !== undefined) lastPlace.set(e.card.id, p);
    });
  });
  let root: HTMLElement | undefined;
  /**
   * Dismiss a card; if focus was on its Dismiss, it moves to the next card's Dismiss, else to the
   * bar's first button, else to the stack itself, never to the page's body.
   */
  const dismiss = (id: number, from: HTMLElement): void => {
    const hadFocus = document.activeElement === from;
    s.dismiss(id);
    if (!hadFocus || root === undefined) return;
    const next =
      root.querySelector<HTMLElement>(
        '.transfer-card:not(.transfer-card--leaving):not(.transfer-card--hidden) .transfer-card__x',
      ) ??
      root.querySelector<HTMLElement>('.fleet-transfers__btn') ??
      root;
    next.focus();
  };
  const measure = (id: number, px: number): void => {
    if (heights().get(id) === px) return;
    setHeights((m) => {
      const live = new Set(s.entries().map((e) => e.card.id));
      const next = new Map([...m].filter(([k]) => live.has(k)));
      return next.set(id, px);
    });
  };
  return (
    <section
      ref={root}
      tabIndex={-1}
      class="fleet-transfers"
      aria-label={t('fleet.transfer.region')}
      onPointerEnter={(e) => {
        if (e.pointerType !== 'touch') s.setHover(true);
      }}
      onPointerLeave={(e) => {
        if (e.pointerType !== 'touch') s.setHover(false);
      }}
      onFocusIn={() => {
        measureRoom();
        s.setFocus(true);
      }}
      onFocusOut={(e) => {
        const to = e.relatedTarget;
        if (to instanceof Node && e.currentTarget.contains(to)) return;
        s.setFocus(false);
      }}
    >
      <Show when={s.entries().length >= 2}>
        <SummaryBar store={s} expanded={expanded()} older={layout().older} onExpand={measureRoom} />
      </Show>
      <div class="fleet-transfers__stack" style={{ height: `${layout().height}px` }}>
        <For each={s.leaving()}>
          {(l) => (
            <TransferCard
              card={l.card}
              state={() => l.state}
              place={() => lastPlace.get(l.card.id)}
              page={props.page}
              accepted={props.accepted}
              reduced={reduced}
              leaving
              onDismiss={() => {}}
              onBehind={() => {}}
            />
          )}
        </For>
        <For each={s.entries()}>
          {(e) => (
            <TransferCard
              card={e.card}
              state={e.state}
              place={() => placeOf(e.card.id)}
              page={props.page}
              accepted={props.accepted}
              reduced={reduced}
              onMeasure={(px) => measure(e.card.id, px)}
              onDismiss={(from) => dismiss(e.card.id, from)}
              onBehind={() => {
                if (!expanded()) {
                  measureRoom();
                  s.pin();
                }
              }}
            />
          )}
        </For>
      </div>
    </section>
  );
}

/** The summary pill, from two cards up: counts, Expand / Collapse and Clear. */
function SummaryBar(props: {
  readonly store: TransferStackStore;
  readonly expanded: boolean;
  readonly older: number;
  readonly onExpand: () => void;
}): JSX.Element {
  const sum = () => props.store.summary();
  const title = (): string =>
    sum().machines > 1
      ? t('fleet.transfer.sum_title_from', { count: sum().total, machines: sum().machines })
      : t('fleet.transfer.sum_title', { count: sum().total });
  const parts = (): JSX.Element[] => {
    const out: JSX.Element[] = [];
    const s = sum();
    if (s.moving > 0) out.push(<b>{t('fleet.transfer.sum_moving', { count: s.moving })}</b>);
    if (s.done > 0) out.push(<span>{t('fleet.transfer.sum_done', { count: s.done })}</span>);
    if (s.held > 0) out.push(<span>{t('fleet.transfer.sum_held', { count: s.held })}</span>);
    if (s.failed > 0) {
      out.push(
        <b class="fleet-transfers__bad">{t('fleet.transfer.sum_failed', { count: s.failed })}</b>,
      );
    }
    if (props.expanded && s.machines > 1) {
      out.push(<span>{t('fleet.transfer.sum_from', { count: s.machines })}</span>);
    }
    if (props.expanded && props.older > 0) {
      out.push(<span>{t('fleet.transfer.sum_older', { count: props.older })}</span>);
    }
    return out;
  };
  return (
    <div class="fleet-transfers__bar" title={title()}>
      <span class="fleet-transfers__sum">
        <span class="fleet-transfers__n">{sum().total}</span>
        <For each={parts()}>
          {(p, i) => (
            <>
              <Show when={i() > 0}>
                <i aria-hidden="true">·</i>
              </Show>
              {p}
            </>
          )}
        </For>
      </span>
      <button
        type="button"
        class="fleet-transfers__btn"
        aria-expanded={props.expanded ? 'true' : 'false'}
        onClick={() => {
          props.onExpand();
          props.store.toggle();
        }}
      >
        {props.expanded ? t('fleet.transfer.collapse') : t('fleet.transfer.expand')}
      </button>
      <button type="button" class="fleet-transfers__btn" onClick={() => props.store.clear()}>
        {t('fleet.transfer.clear')}
      </button>
    </div>
  );
}
